import { PauseActionType } from "@smartrr/shared/entities/CancellationRelationship";
import { getAttemptsTab } from "./getAttemptsTab";
import { getInterceptOutcomesTab } from "./getInterceptOutcomesTab";
import { getInterceptTotalsTab } from "./getInterceptTotalsTab";
import { getReasonSummaryTab } from "./getReasonSummaryTab";
import { GetTabsParams } from "./types";

const getCancelTabs = ({
  isLoadingData,
  noCancellationReasons,
  responseHeader,
  formattedUserReasonTableRows,
  formattedReasonTotalTableRows,
}: GetTabsParams) => [
  getAttemptsTab(isLoadingData, {
    id: "cancellation-attempts-by-customer-1",
    label: "Cancellation attempts",
    headings: ["Customer", "Subscription ID", "Cancelation Reason", "Date", "Action Taken"],
    emptyMessage: "When customers attempt to cancel, their reasons will show here.",
    isEmpty: noCancellationReasons,
    rows: formattedUserReasonTableRows,
  }),
  getReasonSummaryTab(responseHeader, {
    id: "cancellation-reason-summary-1",
    label: "Cancellation summary",
    rows: formattedReasonTotalTableRows,
  }),
];

const getPauseTabs = ({
  isLoadingData,
  responseHeader,
  noPauseReasons,
  formattedUserPauseReasonTableRows,
  formattedPauseReasonTotalTableRows,
  formattedOtherPauseReasonTableRows,
}: GetTabsParams) => [
  getAttemptsTab(isLoadingData, {
    id: "pause-attempts-summary-1",
    label: "Pause attempts",
    headings: ["Customer", "Subscription ID", "Pause Reason", "Date", "Action Taken"],
    emptyMessage: "When customers attempt to pause, their reasons will show here.",
    isEmpty: noPauseReasons(PauseActionType.PAUSE),
    rows: formattedUserPauseReasonTableRows(PauseActionType.PAUSE),
  }),
  getReasonSummaryTab(responseHeader, {
    id: "pause-reason-summary-1",
    label: "Pause summary",
    rows: formattedPauseReasonTotalTableRows(PauseActionType.PAUSE),
  }),
  getAttemptsTab(isLoadingData, {
    id: "skip-attempts-summary-1",
    label: "Skip order attempts",
    headings: ["Customer", "Subscription ID", "Skip Reason", "Date", "Action Taken"],
    emptyMessage: "When customers attempt to skip an order, their reasons will show here.",
    isEmpty: noPauseReasons(PauseActionType.SKIP),
    rows: formattedOtherPauseReasonTableRows(PauseActionType.SKIP, "Skipped"),
  }),
  getReasonSummaryTab(responseHeader, {
    id: "skip-reason-summary-1",
    label: "Skip order summary",
    rows: formattedPauseReasonTotalTableRows(PauseActionType.SKIP),
  }),
  getAttemptsTab(isLoadingData, {
    id: "delay-attempts-summary-1",
    label: "Set next order date attempts",
    headings: ["Customer", "Subscription ID", "Set next order date reason", "Date", "Action Taken"],
    emptyMessage: "When customers attempt to delay an order, their reasons will show here.",
    isEmpty: noPauseReasons(PauseActionType.CHANGE_DATE),
    rows: formattedOtherPauseReasonTableRows(PauseActionType.CHANGE_DATE, "Date changed"),
  }),
  getReasonSummaryTab(responseHeader, {
    id: "delay-reason-summary-1",
    label: "Set next order date summary",
    rows: formattedPauseReasonTotalTableRows(PauseActionType.CHANGE_DATE),
  }),
];

const getInterceptTabs = ({
  isLoadingData,
  interceptOutcomes,
  formattedUserCancellationInterceptOutcomeTotals,
}: GetTabsParams) => [
  getInterceptOutcomesTab(isLoadingData, {
    id: "cancellation-intercept-outcomes",
    label: "User Cancellation Intercept Outcomes",
    headings: ["Customer", "Subscription ID", "Date", "Intercept outcome"],
    emptyMessage:
      "When customers attempt to cancel a subscription, the cancellation flow outcomes will show here.",
    ...interceptOutcomes,
  }),
  getInterceptTotalsTab(isLoadingData, {
    id: "cancellation-intercept-totals",
    label: "User Cancellation Intercept Totals",
    emptyMessage:
      "When customers attempt to cancel a subscription, the cancellation flow outcome totals will show here.",
    rows: formattedUserCancellationInterceptOutcomeTotals,
  }),
];

type RetentionTab =
  | ReturnType<typeof getAttemptsTab>
  | ReturnType<typeof getReasonSummaryTab>
  | ReturnType<typeof getInterceptOutcomesTab>
  | ReturnType<typeof getInterceptTotalsTab>;

export const getTabs = (params: GetTabsParams): RetentionTab[] => {
  switch (params.section) {
    case "all": {
      return [...getCancelTabs(params), ...getPauseTabs(params), ...getInterceptTabs(params)];
    }
    case "cancel": {
      return getCancelTabs(params);
    }
    case "pause": {
      return getPauseTabs(params);
    }
    case "intercept": {
      return getInterceptTabs(params);
    }
  }
};
